'use client'

import { useTranslations } from 'next-intl'

import { Flex } from '@radix-ui/themes'
import { useState } from 'react'
import { ConfirmButton, CancelButton } from '../../primitives'

type TProps = {
    closeText?: string
    onClose?: () => void
    confirmText?: string
    onConfirm?: () => void | Promise<void>
    submitButton?: boolean
    disable?: boolean
}

/**
 * Standardized modal footer with consistent button ordering:
 * Cancel (left) → Confirm (right)
 */
const ModalFooter = ({
    closeText,
    onClose,
    confirmText,
    onConfirm,
    submitButton = false,
    disable = false,
}: TProps) => {
    const t = useTranslations('modal.footer')
    const [disabled, setDisabled] = useState(false)

    return (
        <Flex pt="5" gap="4" justify="end">
            {onClose && (
                <CancelButton
                    disabled={disable || disabled}
                    onClick={onClose}
                >
                    {closeText ?? t('close')}
                </CancelButton>
            )}
            {(onConfirm || submitButton) && (
                <ConfirmButton
                    disabled={disable || disabled}
                    type={submitButton ? 'submit' : 'button'}
                    onClick={async () => {
                        setDisabled(true)
                        try {
                            await onConfirm?.()
                        }
                        catch (e) {
                            console.error(e)
                        }
                        finally {
                            setDisabled(false)
                        }
                    }}
                >
                    {confirmText ?? t('confirm')}
                </ConfirmButton>
            )}
        </Flex>
    )
}

export default ModalFooter
